import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUsersByID,
  getUserAvailablePromotion,
} from "../../store/slices/user";
import useAuth from "../../hooks/useAuth";
import axios from "axios";
import scbIcon from "../../assets/bankicon/014.webp";
import truemoneyIcon from "../../assets/bankicon/true.webp";
import ktbIcon from "../../assets/bankicon/006.webp";
import kbankIcon from "../../assets/bankicon/004.webp";
import bblIcon from "../../assets/bankicon/002.webp";
import ttbIcon from "../../assets/bankicon/011.webp";
import gsbIcon from "../../assets/bankicon/030.webp";
import krungsriIcon from "../../assets/bankicon/025.webp";
import baacIcon from "../../assets/bankicon/034.webp";
import uobIcon from "../../assets/bankicon/024.webp";
import ghbIcon from "../../assets/bankicon/033.webp";
import cimbIcon from "../../assets/bankicon/022.webp";
import islamIcon from "../../assets/bankicon/066.webp";
import promptpayIcon from "../../assets/bankicon/promptpay.webp";
import "./Deposit.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3033";

export default function Deposit() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { singleUsers, userAvailablePromotion } = useSelector((s) => s.user);

  const [method, setMethod] = useState("onpay");
  const [amount, setAmount] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [bibpayData, setBibpayData] = useState(null);
  const [bibpayError, setBibpayError] = useState("");
  const [onpayError, setOnpayError] = useState("");
  const [bibpayLoading, setBibpayLoading] = useState(false);
  const [bankData, setBankData] = useState([]);
  const [helper, setHelper] = useState(false);
  const [helper2, setHelper2] = useState(false);
  const [depositMethods, setDepositMethods] = useState([
    {
      id: "onpay",
      name: "ฝากออโต้ Qrcode",
      icon: "solar:card-bold",
      color: "#FF6B35",
      available: false,
    },
    {
      id: "bibpay",
      name: "ฝากออโต้ Qrcode",
      icon: "solar:wallet-bold",
      color: "#4ECDC4",
      available: false,
    },
    {
      id: "autodeposit",
      name: "ฝากออโต้",
      icon: "solar:banknote-2-bold-duotone",
      color: "#45B7D1",
      available: false,
    },

    {
      id: "truemoney",
      name: "ทรูมันนี่วอลเลท",
      icon: "solar:smartphone-2-bold",
      color: "#96CEB4",
      available: false,
    },
  ]);
  const intervalRef = useRef(null);

  const account = Array.isArray(singleUsers) ? singleUsers[0] : null;

  // Determine user id (from current user slice, localStorage, or account)
  const storedId =
    user?.id || // prefer authenticated user id
    account?.id ||
    (typeof window !== "undefined" ? localStorage.getItem("userId") : null);

  // Helper function to get bank logo based on bank code
  const getBankLogo = (bankCode) => {
    switch (bankCode) {
      case "true":
        return truemoneyIcon;
      case "014":
        return scbIcon;
      case "006":
        return ktbIcon;
      case "004":
        return kbankIcon;
      case "002":
        return bblIcon;
      case "011":
        return ttbIcon;
      case "030":
        return gsbIcon;
      case "025":
        return krungsriIcon;
      case "034":
        return baacIcon;
      case "024":
        return uobIcon;
      case "033":
        return ghbIcon;
      case "022":
        return cimbIcon;
      case "066":
        return islamIcon;
      case "promptpay":
        return promptpayIcon;
      default:
        return scbIcon; // fallback
    }
  };

  // Helper function to get bank name based on bank code
  const getBankName = (bankCode) => {
    switch (bankCode) {
      case "true":
        return "ทรูมันนี่วอลเล็ท";
      case "014":
        return "ธนาคารไทยพาณิชย์";
      case "006":
        return "ธนาคารกรุงไทย";
      case "004":
        return "ธนาคารกสิกรไทย";
      case "002":
        return "ธนาคารกรุงเทพ";
      case "011":
        return "ธนาคารทีทีบี";
      case "030":
        return "ธนาคารออมสิน";
      case "025":
        return "ธนาคารกรุงศรี";
      case "034":
        return "ธนาคารธ.ก.ส.";
      case "024":
        return "ธนาคารUOB";
      case "033":
        return "ธนาคารอาคารสงเคราะห์";
      case "022":
        return "ธนาคารCIMB";
      case "066":
        return "ธนาคารอิสลาม";
      case "promptpay":
        return "พร้อมเพย์";
      default:
        return "ธนาคาร";
    }
  };

  const quickAmounts = [100, 300, 500, 1000];

  const getMinDeposit = (depositMethod) =>
    depositMethod === "onpay" ? 100 : 10;

  // Mock user data
  const mockUser = {
    id: 64592,
    username: "0888888888",
    name: "สมชาย อยู่ดี",
  };

  // Fetch bank data from API
  useEffect(() => {
    const fetchBankData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/bank`);
        if (response.data && response.data.data) {
          setBankData(response.data.data);
          updateDepositMethodsAvailability(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching bank data:", error);
      }
    };

    fetchBankData();
  }, []);

  // Fetch user data from API
  useEffect(() => {
    if (storedId) dispatch(getUsersByID({ id: storedId }));
  }, [storedId, dispatch]);

  useEffect(() => {
    if (storedId) dispatch(getUserAvailablePromotion(storedId));
  }, [storedId, dispatch]);

  // Update deposit methods availability based on bank data
  const updateDepositMethodsAvailability = (banks) => {
    const isOnpayActive = banks.some(
      (bank) =>
        (bank.bankname === "onpay" || bank.bankname === "fairpay") &&
        bank.status === "ACTIVE",
    );
    const isBibpayActive = banks.some(
      (bank) => bank.bankname === "bibpay" && bank.status === "ACTIVE",
    );
    const isAutoDepositActive = banks.some(
      (bank) => bank.bankname === "scb" && bank.status === "ACTIVE",
    );
    const isTruemoneyActive = banks.some(
      (bank) => bank.bankname === "true" && bank.status === "ACTIVE",
    );

    setDepositMethods((prev) =>
      prev.map((method) => ({
        ...method,
        available:
          (method.id === "onpay" && isOnpayActive) ||
          (method.id === "bibpay" && isBibpayActive) ||
          (method.id === "autodeposit" && isAutoDepositActive) ||
          (method.id === "truemoney" && isTruemoneyActive),
      })),
    );

    // Default tab when open: prefer "onpay" if available.
    // Otherwise, keep current selection if it's still available; else fall back.
    const availability = {
      onpay: isOnpayActive,
      bibpay: isBibpayActive,
      autodeposit: isAutoDepositActive,
      truemoney: isTruemoneyActive,
    };

    const fallbackOrder = ["onpay", "autodeposit", "truemoney", "bibpay"];

    setMethod((current) => {
      if (availability.onpay) return "onpay";
      if (availability[current]) return current;
      return fallbackOrder.find((id) => availability[id]) || current;
    });
  };

  useEffect(() => {
    if (countdown === 0 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [countdown]);

  const generateQr = async () => {
    const minDeposit = getMinDeposit(method);
    const a = Number(amount) || 0;
    if (a < minDeposit) return;

    setIsGenerating(true);
    setOnpayError("");

    try {
      const amountNum = parseInt(String(amount), 10);
      if (!Number.isFinite(amountNum) || amountNum < minDeposit) {
        setOnpayError(
          `กรุณากรอกจำนวนเงินเป็นจำนวนเต็มอย่างน้อย ${minDeposit} บาท`,
        );
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/onpay/deposit/create-qr-code`,
        {
          amount: String(amountNum),
          userId: storedId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      const payload = response.data;
      const qrUrl =
        payload?.qr_code_url ||
        payload?.data?.qr_code_url ||
        payload?.qrCodeUrl ||
        payload?.data?.qrCodeUrl;
      const qrBase64 =
        payload?.qr_code ||
        payload?.data?.qr_code ||
        payload?.qrCode ||
        payload?.data?.qrCode;

      const imageSrc = qrUrl
        ? String(qrUrl)
        : qrBase64
          ? `data:image/png;base64,${String(qrBase64)}`
          : null;

      if (!imageSrc) {
        throw new Error(
          payload?.message || "ไม่พบข้อมูล QR Code จากระบบชำระเงิน",
        );
      }

      setQrDataUrl(imageSrc);

      // Start countdown (5 minutes)
      setCountdown(300);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Onpay error:", error);
      setOnpayError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "เกิดข้อผิดพลาดในการสร้าง QR Code",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBibpaySubmit = async () => {
    const amountNum = parseInt(amount);
    if (!amount || amountNum < 10 || !Number.isInteger(amountNum)) {
      setBibpayError("กรุณากรอกจำนวนเงินเป็นจำนวนเต็มอย่างน้อย 10 บาท");
      return;
    }

    setBibpayLoading(true);
    setBibpayError("");

    try {
      console.log("Creating BiBPAY charge with:", {
        amount: amountNum,
        userId: mockUser.id,
        username: mockUser.username,
      });

      const response = await axios.post(
        `${API_BASE_URL}/bibbyx/create-charge`,
        {
          amount: amountNum,
          userId: singleUsers?.[0]?.id,
          username: singleUsers?.[0]?.username,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );

      if (response.data && response.data.success) {
        setBibpayData(response.data.data);
        setQrDataUrl(response.data.data.qrCodeImage); // Fixed: access nested qrCodeImage

        // Start countdown (15 minutes)
        setCountdown(900);
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
          setCountdown((c) => {
            if (c <= 1) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
              return 0;
            }
            return c - 1;
          });
        }, 1000);
      } else {
        setBibpayError(
          response.data?.message || "เกิดข้อผิดพลาดในการสร้างธุรกรรม",
        );
      }
    } catch (error) {
      console.error("BiBPAY error:", error);

      if (error.code === "ERR_NETWORK") {
        setBibpayError("ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้");
      } else if (error.response?.status === 404) {
        setBibpayError("ไม่พบบริการที่ร้องขอ");
      } else if (error.response?.status === 503) {
        setBibpayError("ระบบชำระเงินไม่พร้อมใช้งาน กรุณาลองใหม่อีกครั้ง");
      } else if (error.response?.status === 500) {
        setBibpayError("เกิดข้อผิดพลาดภายในเซิร์ฟเวอร์");
      } else {
        setBibpayError(
          error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "เกิดข้อผิดพลาดในการเชื่อมต่อ",
        );
      }
    } finally {
      setBibpayLoading(false);
    }
  };

  const saveQr = () => {
    if (!qrDataUrl) return;
    const link = document.createElement("a");
    link.href = qrDataUrl;
    link.download = `deposit_${method}_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const formatTime = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const selectedMethodData = depositMethods.find((m) => m.id === method);

  const resetAll = () => {
    setQrDataUrl(null);
    setCountdown(0);
    setAmount("");
    setBibpayData(null);
    setBibpayError("");
    setOnpayError("");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const promotionDetail = singleUsers?.[0]?.PromotionDetail;
  const promotionStatus = singleUsers?.[0]?.PromotionStatus;
  const promotionID = singleUsers?.[0]?.PromotionID;
  const receivePromo = userAvailablePromotion?.find(
    (item) => item.PromotionID === promotionID,
  );

  return (
    <div
      style={{
        padding: "20px 16px",
        background: "#ffffff",
        borderRadius: "12px",
        color: "#111827",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Method Selection */}
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            fontSize: "16px",
            marginBottom: "12px",
            textAlign: "center",
            color: "#111827",
            fontWeight: "600",
          }}
        >
          เลือกช่องทางการฝากเงิน
        </div>
        <div className="deposit-methods-grid">
          {depositMethods
            .filter((m) => m.available)
            .map((m) => (
              <button
                key={m.id}
                onClick={() => setMethod(m.id)}
                style={{
                  padding: "12px 8px",
                  borderRadius: "8px",
                  border:
                    method === m.id
                      ? "2px solid var(--primaryColor)"
                      : "1px solid #e5e7eb",
                  background: method === m.id ? "#fef3c7" : "#f9fafb",
                  color: method === m.id ? "#111827" : "#374151",
                  cursor: "pointer",
                  fontSize: "14px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                  transition: "all 0.2s ease",
                }}
              >
                <Icon
                  icon={m.icon}
                  width={20}
                  height={20}
                  style={{ color: method === m.id ? "#111827" : "#374151" }}
                />
                <span
                  style={{ color: method === m.id ? "#111827" : "#374151" }}
                >
                  {m.name}
                </span>
              </button>
            ))}
        </div>
        {promotionDetail && (
          <div
            style={{
              marginTop: "16px",
              background: "#fef3c7",
              borderRadius: "12px",
              padding: "16px 18px",
              color: "#92400e",
              fontSize: "14px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              border: "1px solid #fcd34d",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-50%",
                right: "-20%",
                width: "200px",
                height: "200px",
                background:
                  "radial-gradient(circle, rgba(255, 217, 61, 0.1) 0%, transparent 70%)",
                borderRadius: "50%",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "10px",
                position: "relative",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  background: "#fbbf24",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  flexShrink: 0,
                }}
              >
                <Icon icon="mdi:gift" width={24} height={24} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.9,
                    marginBottom: "4px",
                  }}
                >
                  โปรโมชั่นที่เลือก
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#111827",
                    }}
                  >
                    {promotionDetail}
                  </div>
                  {promotionStatus === "ACTIVE" && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        background: "rgba(76, 175, 80, 0.2)",
                        color: "#4caf50",
                        padding: "2px 10px",
                        borderRadius: "999px",
                        fontWeight: "600",
                        border: "1px solid rgba(76, 175, 80, 0.3)",
                        textAlign: "center",
                        minWidth: "fit-content",
                      }}
                    >
                      กำลังใช้งาน
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div
              style={{
                color: "#78350f",
                fontSize: "12px",
                paddingLeft: "52px",
                lineHeight: "1.5",
                position: "relative",
                zIndex: 1,
              }}
            >
              💡 กรุณาฝากตามเงื่อนไขโปรโมชั่นเพื่อรับโบนัส
            </div>
          </div>
        )}
        {receivePromo && (
          <div
            style={{
              marginTop: "12px",
              background: "#dbeafe",
              borderRadius: "12px",
              padding: "14px 16px",
              color: "#1e40af",
              fontSize: "13px",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              border: "1px solid #93c5fd",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: "-50%",
                left: "-20%",
                width: "200px",
                height: "200px",
                background:
                  "radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)",
                borderRadius: "50%",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                width: "36px",
                height: "36px",
                background: "#3b82f6",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Icon
                icon="mdi:information-outline"
                width={20}
                height={20}
                color="#fff"
              />
            </div>
            <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  marginBottom: "6px",
                  color: "#111827",
                }}
              >
                จำนวนเงินที่แนะนำในการฝาก
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#1e40af",
                  marginBottom: "6px",
                  fontFamily: "monospace",
                  letterSpacing: "0.5px",
                }}
              >
                ฿{receivePromo.BonusDepMaximum.toLocaleString()}
              </div>
              <div
                style={{
                  color: "#1e40af",
                  fontSize: "12px",
                  lineHeight: "1.4",
                }}
              >
                ระบบจะใช้ยอดนี้ในการคำนวณโบนัสตามเงื่อนไขโปรโมชั่น
              </div>
            </div>
          </div>
        )}
        {/* Show promotion detail here */}
      </div>

      {/* Selected Method Display */}
      {selectedMethodData &&
        selectedMethodData.available &&
        !qrDataUrl &&
        (method === "onpay" || method === "bibpay") && (
          <div
            style={{
              background:
                method === "bibpay"
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "12px",
                justifyContent: "center",
              }}
            >
              <div>
                <Icon icon={selectedMethodData.icon} width={32} height={32} />
                <div style={{ fontSize: "16px", fontWeight: "600" }}>
                  {selectedMethodData.name}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.9 }}>
                  ฝากขั้นต่ำ {method === "onpay" ? "100" : "10"} บาท
                  สำหรับการบริการที่รวดเร็ว
                </div>
              </div>
            </div>

            {/* Amount Input */}
            <div style={{ marginBottom: "12px" }}>
              <div style={{ fontSize: "14px", marginBottom: "8px" }}>
                จำนวนเงินที่ต้องการฝาก (บาท) ขั้นต่ำ{" "}
                {method === "onpay" ? "100" : "10"} บาท
              </div>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="กรุณาระบุจำนวนเงิน"
                type="number"
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #d1d5db",
                  background: "#f9fafb",
                  color: "#111827",
                  fontSize: "18px",
                  textAlign: "center",
                  fontWeight: "600",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Quick Amount Buttons */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: "16px",
                flexWrap: "wrap",
              }}
            >
              {quickAmounts.map((amt) => (
                <button
                  key={amt}
                  onClick={() => setAmount(amt.toString())}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    color: "#374151",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  ฿{amt}
                </button>
              ))}
            </div>

            {/* Error Display */}
            {method === "bibpay" && bibpayError && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "8px",
                  background: "rgba(255,0,0,0.2)",
                  borderRadius: "6px",
                  fontSize: "12px",
                }}
              >
                {bibpayError}
              </div>
            )}

            {method === "onpay" && onpayError && (
              <div
                style={{
                  marginBottom: "12px",
                  padding: "8px",
                  background: "rgba(255,0,0,0.2)",
                  borderRadius: "6px",
                  fontSize: "12px",
                }}
              >
                {onpayError}
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={method === "bibpay" ? handleBibpaySubmit : generateQr}
              disabled={
                (method === "bibpay" ? bibpayLoading : isGenerating) ||
                Number(amount) < (method === "onpay" ? 100 : 10)
              }
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "none",
                background:
                  Number(amount) >= (method === "onpay" ? 100 : 10)
                    ? "#28a745"
                    : "#6c757d",
                color: "#fff",
                fontSize: "16px",
                fontWeight: "600",
                cursor:
                  Number(amount) >= (method === "onpay" ? 100 : 10)
                    ? "pointer"
                    : "not-allowed",
                opacity: (method === "bibpay" ? bibpayLoading : isGenerating)
                  ? 0.7
                  : 1,
              }}
            >
              {(method === "bibpay" ? bibpayLoading : isGenerating)
                ? "กำลังสร้าง QR Code..."
                : "สร้าง QR Code"}
            </button>
          </div>
        )}

      {/* Auto Deposit Bank Information Display */}
      {selectedMethodData &&
        selectedMethodData.available &&
        method === "autodeposit" && (
          <div
            style={{
              background: "linear-gradient(135deg, #8e44ad 0%, #3b1e54 100%)",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <div>
                <Icon icon={selectedMethodData.icon} width={32} height={32} />
                <div style={{ fontSize: "16px", fontWeight: "600" }}>
                  {selectedMethodData.name}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.9 }}>
                  โอนเงินเข้าบัญชีธนาคารด้านล่าง
                </div>
              </div>
            </div>

            {/* Bank Account Information */}
            {bankData
              .filter(
                (data) =>
                  data.isMain === "MAINBANK" && data.status === "ACTIVE",
              )
              .map((row) => (
                <div
                  key={row.banknumber}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    padding: "16px",
                    marginBottom: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      ธนาคาร
                    </div>
                    <div
                      className="deposit-responsive-row"
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={scbIcon}
                        alt="SCB Bank"
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                          display: "block",
                        }}
                      />
                      <span style={{ lineHeight: "1.2" }}>
                        ธนาคารไทยพาณิชย์ (SCB)
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      เลขบัญชี
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                        letterSpacing: "1px",
                      }}
                    >
                      {row.banknumber}
                    </div>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      ชื่อบัญชี
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: "600" }}>
                      {row.bankownername}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(row.banknumber);
                      setHelper2(true);
                      setTimeout(() => setHelper2(false), 2000);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      background: helper2 ? "#d1fae5" : "#f9fafb",
                      color: helper2 ? "#065f46" : "#374151",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <Icon
                      icon={
                        helper2 ? "solar:check-circle-bold" : "solar:copy-bold"
                      }
                      width={16}
                      height={16}
                    />
                    {helper2 ? "คัดลอกแล้ว" : "คัดลอกเลขบัญชี"}
                  </button>
                </div>
              ))}

            {/* Instructions */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "8px",
                padding: "12px",
                fontSize: "12px",
                lineHeight: "1.5",
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>
                📌 วิธีการฝากเงิน:
              </div>
              <div style={{ opacity: 0.9 }}>
                1. โอนเงินเข้าบัญชีข้างต้น
                <br />
                2. ระบบจะเติมเครดิตให้อัตโนมัติภายใน 1-5 นาที
                <br />
                3. หากเครดิตไม่เข้า กรุณาติดต่อเจ้าหน้าที่
              </div>
            </div>
          </div>
        )}

      {/* TrueMoney Wallet Information Display */}
      {selectedMethodData &&
        selectedMethodData.available &&
        method === "truemoney" && (
          <div
            style={{
              background: "linear-gradient(135deg, #cc6600 0%, #994d00 100%)",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
                justifyContent: "center",
              }}
            >
              <div>
                <Icon icon={selectedMethodData.icon} width={32} height={32} />
                <div style={{ fontSize: "16px", fontWeight: "600" }}>
                  {selectedMethodData.name}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.9 }}>
                  โอนเงินเข้า TrueMoney Wallet ด้านล่าง
                </div>
              </div>
            </div>

            {/* TrueMoney Wallet Information */}
            {bankData
              .filter(
                (data) => data.isMain === "MAINTMN" && data.status === "ACTIVE",
              )
              .map((row) => (
                <div
                  key={row.BanklistID}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    padding: "16px",
                    marginBottom: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      ประเภท
                    </div>
                    <div
                      className="deposit-responsive-row"
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "12px",
                        textAlign: "center",
                      }}
                    >
                      <img
                        src={truemoneyIcon}
                        alt="TrueMoney Wallet"
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                          display: "block",
                        }}
                      />
                      <span style={{ lineHeight: "1.2" }}>
                        TrueMoney Wallet
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      เบอร์โทรศัพท์
                    </div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        fontFamily: "monospace",
                        letterSpacing: "1px",
                      }}
                    >
                      {row.banknumber}
                    </div>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginBottom: "4px",
                      }}
                    >
                      ชื่อเจ้าของ
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: "600" }}>
                      {row.bankownername}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(row.banknumber);
                      setHelper(true);
                      setTimeout(() => setHelper(false), 2000);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      background: helper ? "#d1fae5" : "#f9fafb",
                      color: helper ? "#065f46" : "#374151",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <Icon
                      icon={
                        helper ? "solar:check-circle-bold" : "solar:copy-bold"
                      }
                      width={16}
                      height={16}
                    />
                    {helper ? "คัดลอกแล้ว" : "คัดลอกเบอร์โทร"}
                  </button>
                </div>
              ))}

            {/* Instructions */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "8px",
                padding: "12px",
                fontSize: "12px",
                lineHeight: "1.5",
              }}
            >
              <div style={{ fontWeight: "600", marginBottom: "8px" }}>
                📱 วิธีการฝากเงิน:
              </div>
              <div style={{ opacity: 0.9 }}>
                1. โอนเงินเข้า TrueMoney Wallet ข้างต้น
                <br />
                2. ระบบจะเติมเครดิตให้อัตโนมัติภายใน 1-5 นาที
                <br />
                3. หากเครดิตไม่เข้า กรุณาติดต่อเจ้าหน้าที่
              </div>
            </div>
          </div>
        )}

      {/* QR Code Display */}
      {qrDataUrl && (
        <div
          style={{
            background: "#f9fafb",
            borderRadius: "12px",
            padding: "16px",
            textAlign: "center",
            border: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#111827",
              marginBottom: "12px",
              fontWeight: "600",
            }}
          >
            ✓ สแกน QR Code เพื่อทำธุรกรรม
          </div>

          <div
            style={{
              background: "#fff",
              display: "inline-block",
              padding: "16px",
              borderRadius: "12px",
              marginBottom: "12px",
            }}
          >
            <img
              src={qrDataUrl}
              alt="QR Code"
              width="100%"
              height="auto"
              style={{ display: "block" }}
            />
          </div>

          <div style={{ marginBottom: "12px" }}>
            <div
              style={{ fontSize: "16px", fontWeight: "600", color: "#111827" }}
            >
              จำนวน: {amount} บาท
            </div>
            {method === "bibpay" && bibpayData && (
              <>
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    color: "#374151",
                  }}
                >
                  ธนาคาร: {bibpayData.data?.bank?.name}
                </div>
                <div style={{ fontSize: "14px", color: "#374151" }}>
                  เลขบัญชี: {bibpayData.data?.bankNumber}
                </div>
                <div style={{ fontSize: "14px", color: "#374151" }}>
                  ชื่อบัญชี: {bibpayData.data?.bankName}
                </div>
                <div style={{ fontSize: "14px", color: "#374151" }}>
                  จำนวนที่ต้องโอน: {bibpayData.data?.depositAmount} บาท
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  Ref: {bibpayData.data?.reference}
                </div>
              </>
            )}
            <div
              style={{
                fontSize: "14px",
                color: countdown > 0 ? "#d97706" : "#ef4444",
                fontWeight: "600",
              }}
            >
              {countdown > 0
                ? `เวลาที่เหลือ: ${formatTime(countdown)}`
                : "QR Code หมดอายุแล้ว"}
            </div>
          </div>

          <div
            style={{ display: "flex", gap: "8px", justifyContent: "center" }}
          >
            <button
              onClick={saveQr}
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                background: "#f9fafb",
                color: "#111827",
                cursor: "pointer",
              }}
            >
              บันทึก QR
            </button>
            <button
              onClick={resetAll}
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                background: "#f3f4f6",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              ยกเลิก
            </button>
          </div>
        </div>
      )}

      {/* Bank Information Card */}
      {method !== "truemoney" && (
        <div
          style={{
            marginTop: "20px",
            background: "#f9fafb",
            borderRadius: "12px",
            padding: "16px",
            border: "1px solid #e5e7eb",
            color: "#111827",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "var(--primaryColor)",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  width: "20px",
                  height: "20px",
                  color: "var(--primaryColor)",
                }}
              >
                <path
                  d="M3 21H21M5 21V7L12 3L19 7V21M9 9H15M9 12H15M9 15H15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span style={{ color: "var(--primaryColor)" }}>
                ข้อมูลธนาคาร โปรดใช้บัญชีของท่านในการโอนเงิน
              </span>
            </div>
          </div>

          {account?.book_number ? (
            <div
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "16px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <img
                    src={getBankLogo(account?.bank_name)}
                    alt={getBankName(account?.bank_name)}
                    style={{
                      width: "60px",
                      height: "60px",
                      borderRadius: "4px",
                    }}
                  />
                </div>
                <div style={{ textAlign: "center", flex: 1 }}>
                  <h4
                    style={{
                      margin: "0 0 8px 0",
                      color: "#111827",
                      fontSize: "18px",
                      fontWeight: "600",
                    }}
                  >
                    {getBankName(account?.bank_name)}
                  </h4>
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      color: "#374151",
                      fontSize: "16px",
                      fontFamily: "monospace",
                      letterSpacing: "1px",
                      fontWeight: "600",
                    }}
                  >
                    {account?.book_number}
                  </p>
                  <p
                    style={{
                      margin: "0",
                      color: "#6b7280",
                      fontSize: "14px",
                    }}
                  >
                    ชื่อบัญชี: {account?.book_name}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                textAlign: "center",
                border: "1px dashed #d1d5db",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#f3f4f6",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px auto",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: "24px", height: "24px", color: "#6b7280" }}
                >
                  <path
                    d="M3 21H21M5 21V7L12 3L19 7V21M9 9H15M9 12H15M9 15H15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  color: "#111827",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                ยังไม่มีบัญชีธนาคาร
              </h4>
              <p
                style={{
                  margin: "0",
                  color: "#6b7280",
                  fontSize: "12px",
                }}
              >
                กรุณาเพิ่มข้อมูลบัญชีธนาคารเพื่อทำธุรกรรม
              </p>
            </div>
          )}
        </div>
      )}

      {/* Warning */}
      <div
        style={{
          marginTop: "16px",
          padding: "12px",
          background: "#fee2e2",
          borderRadius: "8px",
          fontSize: "10px",
          lineHeight: "1.4",
          color: "#991b1b",
          border: "1px solid #fecaca",
        }}
      >
        ⚠️ โปรดยืนยัน ชื่อ-สกุลผู้โอน ยอดที่โอน
        ให้ตรงกับระบบก่อนทำการโอนทุกครั้ง
        หากข้อมูลไม่ตรงกับระบบท่านจะไม่ได้รับเครดิต และระบบจะคืนเงินให้ท่านภายใน
        3 วันทำการ หากมีข้อสงสัยกรุณาติดต่อเจ้าหน้าที่
      </div>
    </div>
  );
}
